import React from 'react';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { VoucherLine } from '../../types/voucher.types';
interface VoucherLinesProps {
  lines: VoucherLine[];
  onChange: (lines: VoucherLine[]) => void;
  accounts: Array<{
    value: string;
    label: string;
  }>;
  errors?: Record<number, {
    accountId?: string;
    debit?: string;
    credit?: string;
  }>;
}
export function VoucherLines({
  lines,
  onChange,
  accounts,
  errors = {}
}: VoucherLinesProps) {
  const addLine = () => {
    onChange([...lines, {
      accountId: '',
      debit: 0,
      credit: 0,
      description: ''
    }]);
  };
  const removeLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index));
  };
  const updateLine = (index: number, field: keyof VoucherLine, value: any) => {
    const updated = [...lines];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    onChange(updated);
  };
  const totalDebit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
  return <div className="space-y-4" data-id="element-277">
      <div className="flex items-center justify-between" data-id="element-278">
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-279">
          Transaction Lines
        </h3>
        <Button type="button" size="sm" onClick={addLine} data-id="element-280">
          <PlusIcon className="w-4 h-4 mr-1" data-id="element-281" />
          Add Line
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden" data-id="element-282">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-283">
          <thead className="bg-gray-50" data-id="element-284">
            <tr data-id="element-285">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-286">
                Account
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-287">
                Description
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-288">
                Debit
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-289">
                Credit
              </th>
              <th className="px-4 py-3 w-16" data-id="element-290"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-291">
            {lines.map((line, index) => <tr key={index} data-id="element-292">
                <td className="px-4 py-3" data-id="element-293">
                  <Select options={accounts} value={line.accountId} onChange={e => updateLine(index, 'accountId', e.target.value)} error={errors[index]?.accountId} className="min-w-[200px]" data-id="element-294" />
                </td>
                <td className="px-4 py-3" data-id="element-295">
                  <Input value={line.description} onChange={e => updateLine(index, 'description', e.target.value)} placeholder="Description" data-id="element-296" />
                </td>
                <td className="px-4 py-3" data-id="element-297">
                  <Input type="number" value={line.debit} onChange={e => updateLine(index, 'debit', Number(e.target.value))} error={errors[index]?.debit} className="text-right" min="0" step="0.01" data-id="element-298" />
                </td>
                <td className="px-4 py-3" data-id="element-299">
                  <Input type="number" value={line.credit} onChange={e => updateLine(index, 'credit', Number(e.target.value))} error={errors[index]?.credit} className="text-right" min="0" step="0.01" data-id="element-300" />
                </td>
                <td className="px-4 py-3" data-id="element-301">
                  <button type="button" onClick={() => removeLine(index)} className="text-red-600 hover:text-red-800" data-id="element-302">
                    <TrashIcon className="w-4 h-4" data-id="element-303" />
                  </button>
                </td>
              </tr>)}
          </tbody>
          <tfoot className="bg-gray-50" data-id="element-304">
            <tr data-id="element-305">
              <td colSpan={2} className="px-4 py-3 text-right font-semibold text-gray-900" data-id="element-306">
                Total:
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900" data-id="element-307">
                ${totalDebit.toFixed(2)}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900" data-id="element-308">
                ${totalCredit.toFixed(2)}
              </td>
              <td data-id="element-309"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {!isBalanced && lines.length > 0 && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-310">
          <span className="text-sm font-medium text-red-800" data-id="element-311">
            Warning: Debit and Credit totals must be equal. Difference: $
            {Math.abs(totalDebit - totalCredit).toFixed(2)}
          </span>
        </div>}

      {isBalanced && lines.length > 0 && <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-id="element-312">
          <span className="text-sm font-medium text-green-800" data-id="element-313">
            ✓ Transaction is balanced
          </span>
        </div>}
    </div>;
}